import { useEffect, useRef, useState } from 'react';
import {
  LiveKitRoom,
  ParticipantTile,
  RoomAudioRenderer,
  TrackLoop,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import {
  ConnectionState,
  MediaDeviceFailure,
  ParticipantEvent,
  RoomEvent,
  Track,
} from 'livekit-client';
import toast from 'react-hot-toast';
import type { Call } from '../types';
import useBreakpoint from '../hooks/useBreakpoint';
import fullScreenIcon from '../assets/full-screen-icon.png';

type CallSessionProps = {
  call: Call;
  token: string;
  serverUrl: string;
  onLeave: () => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
};

function StageLayout() {
  const { isMobile, isTablet } = useBreakpoint();
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare]);
  const remoteTracks = tracks.filter((track) => !track.participant.isLocal);
  const localTracks = tracks.filter((track) => track.participant.isLocal);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [showLocalPrimary, setShowLocalPrimary] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const isBrowser = typeof document !== 'undefined';
  const hasRemoteTrack = remoteTracks.length > 0;
  const canSwapPrimary = localTracks.length > 0 && hasRemoteTrack;
  const shouldShowLocalPrimary =
    localTracks.length > 0 && (showLocalPrimary || (isMobile && !hasRemoteTrack));
  const primaryTracks = shouldShowLocalPrimary ? localTracks : remoteTracks;
  const pipTracks = shouldShowLocalPrimary ? remoteTracks : localTracks;
  const showToggleButton = isMobile && canSwapPrimary;
  const showFullscreenButton = isBrowser;
  const showPipContainer = !isMobile && pipTracks.length > 0;
  const pipPadding = isMobile ? '0.35rem 0.5rem' : isTablet ? '0.4rem 0.65rem' : '0.5rem 0.75rem';
  const pipBottomOffset = isMobile ? '0.75rem' : '1.25rem';
  const pipGap = isMobile ? '0.4rem' : '0.75rem';

  const lockLandscapeIfPossible = async () => {
    // Some TS DOM lib versions don't declare ScreenOrientation.lock/unlock.
    const orientation = (window.screen as unknown as { orientation?: unknown } | undefined)?.orientation as
      | { lock?: (orientation: string) => Promise<void> }
      | undefined;
    if (!orientation?.lock) {
      return;
    }
    try {
      await orientation.lock('landscape');
    } catch {
      // Not supported or user gesture requirements not met (common on iOS/Safari).
    }
  };

  const unlockOrientationIfPossible = () => {
    const orientation = (window.screen as unknown as { orientation?: unknown } | undefined)?.orientation as
      | { unlock?: () => void }
      | undefined;
    if (!orientation?.unlock) {
      return;
    }
    try {
      orientation.unlock();
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!isBrowser) {
      return;
    }
    const handleFullscreenChange = () => {
      const activeElement = document.fullscreenElement;
      const nextIsFullscreen = activeElement === stageRef.current;
      setIsFullscreen(nextIsFullscreen);
      if (!nextIsFullscreen) {
        unlockOrientationIfPossible();
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isBrowser]);

  const toggleFullscreen = async () => {
    if (!isBrowser || !stageRef.current) {
      return;
    }
    try {
      const shouldEnter = !(document.fullscreenElement || isPseudoFullscreen);

      if (shouldEnter) {
        try {
          await stageRef.current.requestFullscreen();
          await lockLandscapeIfPossible();
          return;
        } catch {
          // Fallbacks below
        }

        const video = stageRef.current.querySelector('video') as HTMLVideoElement | null;
        if (video) {
          // Some browsers (notably iOS Safari) only support fullscreen on the <video> element.
          try {
            if (video.requestFullscreen) {
              await video.requestFullscreen();
              await lockLandscapeIfPossible();
              return;
            }
          } catch {
            // ignore
          }
          try {
            const webkitEnterFullscreen = (video as unknown as { webkitEnterFullscreen?: () => void }).webkitEnterFullscreen;
            if (webkitEnterFullscreen) {
              webkitEnterFullscreen();
              return;
            }
          } catch {
            // ignore
          }
        }

        // Last resort: pseudo-fullscreen (CSS fixed positioning)
        setIsPseudoFullscreen(true);
        setIsFullscreen(true);
        return;
      }

      if (isPseudoFullscreen) {
        setIsPseudoFullscreen(false);
        setIsFullscreen(false);
        unlockOrientationIfPossible();
        return;
      }

      if (document.fullscreenElement) {
        await document.exitFullscreen();
        unlockOrientationIfPossible();
      }
    } catch (error) {
      console.error('Unable to toggle fullscreen', error);
    }
  };

  const handleSwapPrimary = () => {
    if (!canSwapPrimary) {
      return;
    }
    setShowLocalPrimary((prev) => !prev);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        flex: 1,
        padding: isMobile ? '1rem 1rem 0.75rem' : isTablet ? '1.25rem 1.25rem 1rem' : '1.5rem 1.5rem 1rem',
        alignItems: 'stretch',
      }}
    >
      <div
        style={{
          background: 'rgba(15, 23, 42, 0.6)',
          borderRadius: isPseudoFullscreen ? '0' : '1rem',
          border: isPseudoFullscreen ? 'none' : '1px solid rgba(148, 163, 184, 0.2)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: isMobile ? '260px' : isTablet ? '360px' : '420px',
          flex: 1,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          ...(isPseudoFullscreen
            ? {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 9999,
                background: 'black',
              }
            : null),
        }}
        className={isPseudoFullscreen ? 'call-stage pseudo-fullscreen' : 'call-stage'}
        ref={stageRef}
      >
        {primaryTracks.length > 0 ? (
          <TrackLoop tracks={primaryTracks}>
            <ParticipantTile className="remote-tile" />
          </TrackLoop>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#94a3b8',
              gap: '0.75rem',
              fontSize: '1.1rem',
              letterSpacing: '0.01em',
            }}
          >
            Waiting for remote video...
          </div>
        )}

        {showToggleButton && (
          <button
            type="button"
            onClick={handleSwapPrimary}
            aria-label={shouldShowLocalPrimary ? 'Show remote video' : 'Show my camera preview'}
            title={shouldShowLocalPrimary ? 'Show remote video' : 'Show my camera preview'}
            style={{
              position: 'absolute',
              right: '0.85rem',
              bottom: '0.85rem',
              borderRadius: '9999px',
              border: '1px solid rgba(148, 163, 184, 0.35)',
              background: 'rgba(15, 23, 42, 0.7)',
              color: '#f8fafc',
              fontSize: '0.7rem',
              fontWeight: 600,
              padding: '0.45rem 0.75rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              boxShadow: '0 18px 28px -22px rgba(30, 64, 175, 0.6)',
            }}
          >
            {shouldShowLocalPrimary ? 'View Remote' : 'View My Camera'}
          </button>
        )}

        {showFullscreenButton && (
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            style={{
              position: 'absolute',
              right: '0.85rem',
              top: '0.85rem',
              borderRadius: '9999px',
              border: '1px solid rgba(148, 163, 184, 0.35)',
              background: 'rgba(15, 23, 42, 0.74)',
              padding: '0.4rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 18px 28px -22px rgba(30, 64, 175, 0.6)',
            }}
          >
            <img
              src={fullScreenIcon}
              alt=""
              style={{
                width: isMobile ? '1.05rem' : '1.25rem',
                height: isMobile ? '1.05rem' : '1.25rem',
                filter: 'drop-shadow(0 4px 12px rgba(14, 23, 42, 0.45))',
              }}
            />
          </button>
        )}

        {showPipContainer && (
          <div
            className="pip-container"
            style={{
              bottom: pipBottomOffset,
              gap: pipGap,
              padding: pipPadding,
              cursor: canSwapPrimary ? 'pointer' : 'default',
            }}
            onDoubleClick={handleSwapPrimary}
            onKeyDown={(event) => {
              if (!canSwapPrimary) {
                return;
              }
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSwapPrimary();
              }
            }}
            role={canSwapPrimary ? 'button' : undefined}
            tabIndex={canSwapPrimary ? 0 : undefined}
            aria-label={
              canSwapPrimary
                ? shouldShowLocalPrimary
                  ? 'Double-click to focus remote participant'
                  : 'Double-click to focus your camera'
                : undefined
            }
          >
            <TrackLoop tracks={pipTracks}>
              <ParticipantTile className="pip-tile" />
            </TrackLoop>
          </div>
        )}
      </div>
    </div>
  );
}

function buildMediaErrorMessage(error: unknown, deviceLabel: 'microphone' | 'camera') {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
      return `Permission to use your ${deviceLabel} was denied. Please allow access in your browser settings.`;
    }
    if (error.name === 'NotFoundError' || error.name === 'OverconstrainedError') {
      return `No ${deviceLabel} was found. Please make sure a ${deviceLabel} is connected and try again.`;
    }
    if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      return `Your ${deviceLabel} appears to be in use by another application. Close it and try again.`;
    }
  }

  if (error && typeof error === 'object') {
    const name = (error as { name?: string }).name;
    if (name === 'AbortError') {
      return `Access to your ${deviceLabel} was interrupted. Please try again.`;
    }
  }

  return `Unable to access your ${deviceLabel}. Please verify your browser permissions and device availability.`;
}

function SessionControls({ call, onLeave }: { call: Call; onLeave: () => void }) {
  const { isMobile, isTablet } = useBreakpoint();
  const room = useRoomContext();
  const [micEnabled, setMicEnabled] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const initialMediaAttemptRef = useRef(false);
  const lastCallIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastCallIdRef.current !== call.callId) {
      lastCallIdRef.current = call.callId;
      initialMediaAttemptRef.current = false;
    }
  }, [call.callId]);

  useEffect(() => {
    if (!room) {
      setMicEnabled(false);
      setCameraEnabled(false);
      return;
    }
    const participant = room.localParticipant;
    const syncStates = () => {
      setMicEnabled(participant.isMicrophoneEnabled);
      setCameraEnabled(participant.isCameraEnabled);
    };
    syncStates();
    participant.on(ParticipantEvent.TrackMuted, syncStates);
    participant.on(ParticipantEvent.TrackUnmuted, syncStates);
    participant.on(ParticipantEvent.TrackPublished, syncStates);
    participant.on(ParticipantEvent.TrackUnpublished, syncStates);
    participant.on(ParticipantEvent.LocalTrackPublished, syncStates);
    participant.on(ParticipantEvent.LocalTrackUnpublished, syncStates);
    return () => {
      participant.off(ParticipantEvent.TrackMuted, syncStates);
      participant.off(ParticipantEvent.TrackUnmuted, syncStates);
      participant.off(ParticipantEvent.TrackPublished, syncStates);
      participant.off(ParticipantEvent.TrackUnpublished, syncStates);
      participant.off(ParticipantEvent.LocalTrackPublished, syncStates);
      participant.off(ParticipantEvent.LocalTrackUnpublished, syncStates);
    };
  }, [room]);

  useEffect(() => {
    if (!room) {
      return;
    }

    const participant = room.localParticipant;

    const enableInitialMedia = async () => {
      if (initialMediaAttemptRef.current || room.state !== ConnectionState.Connected) {
        return;
      }
      initialMediaAttemptRef.current = true;

      try {
        await participant.setMicrophoneEnabled(true);
      } catch (error) {
        console.error('Unable to enable microphone on join', error);
        toast.error(buildMediaErrorMessage(error, 'microphone'));
      } finally {
        setMicEnabled(participant.isMicrophoneEnabled);
      }

      try {
        await participant.setCameraEnabled(true);
      } catch (error) {
        console.error('Unable to enable camera on join', error);
        toast.error(buildMediaErrorMessage(error, 'camera'));
      } finally {
        setCameraEnabled(participant.isCameraEnabled);
      }
    };

    void enableInitialMedia();

    const handleConnectionChange = () => {
      void enableInitialMedia();
    };

    room.on(RoomEvent.ConnectionStateChanged, handleConnectionChange);

    return () => {
      room.off(RoomEvent.ConnectionStateChanged, handleConnectionChange);
    };
  }, [room]);

  const toggleMic = async () => {
    if (!room) {
      return;
    }
    const participant = room.localParticipant;
    const next = !participant.isMicrophoneEnabled;
    try {
      await participant.setMicrophoneEnabled(next);
      setMicEnabled(next);
    } catch (error) {
      console.error('Unable to toggle microphone', error);
      toast.error(buildMediaErrorMessage(error, 'microphone'));
      setMicEnabled(participant.isMicrophoneEnabled);
    }
  };

  const toggleCamera = async () => {
    if (!room) {
      return;
    }
    const participant = room.localParticipant;
    const next = !participant.isCameraEnabled;
    try {
      await participant.setCameraEnabled(next);
      setCameraEnabled(next);
    } catch (error) {
      console.error('Unable to toggle camera', error);
      toast.error(buildMediaErrorMessage(error, 'camera'));
      setCameraEnabled(participant.isCameraEnabled);
    }
  };

  const handleLeave = async () => {
    if (room) {
      await room.disconnect(true);
    }
    onLeave();
  };

  const agentLabel = call.agentName?.trim();
  const locationLabel = call.deploymentLocation?.trim();
  const showMetadata = Boolean(agentLabel || locationLabel);

  return (
    <div style={{ padding: isMobile ? '0 1rem 1rem' : isTablet ? '0 1.25rem 1.25rem' : '0 1.5rem 1.5rem' }}>
      {showMetadata && (
        <div
          style={{
            marginBottom: '0.85rem',
            color: '#cbd5f5',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '0.5rem' : '1.25rem',
            textAlign: isMobile ? 'left' : 'center',
            fontSize: '0.85rem',
            letterSpacing: '0.01em',
          }}
        >
          {agentLabel && (
            <div>
              <div
                style={{
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontSize: '0.7rem',
                  marginBottom: '0.15rem',
                }}
              >
                Agent
              </div>
              <div style={{ fontWeight: 600 }}>{agentLabel}</div>
            </div>
          )}
          {locationLabel && (
            <div>
              <div
                style={{
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontSize: '0.7rem',
                  marginBottom: '0.15rem',
                }}
              >
                Deployment
              </div>
              <div style={{ fontWeight: 600 }}>{locationLabel}</div>
            </div>
          )}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'flex-start' : 'center',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '0.75rem',
          flexWrap: isTablet && !isMobile ? 'wrap' : 'nowrap',
        }}
      >
        <button
          type="button"
          onClick={() => void toggleMic()}
          style={{
            borderRadius: '9999px',
            border: '1px solid rgba(148, 163, 184, 0.25)',
            padding: '0.75rem 1.5rem',
            background: micEnabled ? 'rgba(22, 163, 74, 0.2)' : 'rgba(15, 23, 42, 0.85)',
            color: micEnabled ? '#bbf7d0' : '#e2e8f0',
            fontWeight: 600,
            letterSpacing: '0.03em',
            cursor: 'pointer',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          {micEnabled ? 'Mute' : 'Unmute'}
        </button>
        <button
          type="button"
          onClick={() => void toggleCamera()}
          style={{
            borderRadius: '9999px',
            border: '1px solid rgba(148, 163, 184, 0.25)',
            padding: '0.75rem 1.5rem',
            background: cameraEnabled ? 'rgba(59, 130, 246, 0.2)' : 'rgba(15, 23, 42, 0.85)',
            color: cameraEnabled ? '#bfdbfe' : '#e2e8f0',
            fontWeight: 600,
            letterSpacing: '0.03em',
            cursor: 'pointer',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          {cameraEnabled ? 'Camera Off' : 'Camera On'}
        </button>
        <button
          type="button"
          onClick={() => void handleLeave()}
          style={{
            borderRadius: '9999px',
            border: '1px solid rgba(239, 68, 68, 0.6)',
            padding: '0.75rem 1.5rem',
            background: 'rgba(239, 68, 68, 0.85)',
            color: '#fff',
            fontWeight: 700,
            letterSpacing: '0.05em',
            cursor: 'pointer',
            boxShadow: '0 20px 45px -20px rgba(239, 68, 68, 0.8)',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          Leave
        </button>
      </div>
    </div>
  );
}

function ConnectionBanner({ call, state }: { call: Call; state: ConnectionState }) {
  const { isMobile, isTablet } = useBreakpoint();
  const targetName = call.agentName ?? call.displayName ?? 'caller';
  let label = '';
  if (state === ConnectionState.Connecting) {
    label = `Connecting to ${targetName}...`;
  } else if (state === ConnectionState.Reconnecting) {
    label = 'Reconnecting...';
  }

  if (!label) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: isMobile ? '0.75rem' : '1.25rem',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: isMobile ? '0.4rem 1rem' : '0.45rem 1.25rem',
        borderRadius: '9999px',
        background: state === ConnectionState.Connected ? 'rgba(22, 163, 74, 0.8)' : 'rgba(59, 130, 246, 0.85)',
        color: 'white',
        fontWeight: 500,
        letterSpacing: '0.02em',
        boxShadow: '0 20px 35px -20px rgba(59, 130, 246, 0.8)',
        fontSize: isMobile ? '0.75rem' : '0.85rem',
        maxWidth: isMobile || isTablet ? 'calc(100% - 2rem)' : '420px',
        textAlign: 'center',
        zIndex: 10,
        pointerEvents: 'none',
      }}
    >
      {label}
    </div>
  );
}

export default function CallSession({ call, token, serverUrl, onLeave, onConnectionStateChange }: CallSessionProps) {
  return (
    <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <LiveKitRoom
        serverUrl={serverUrl}
        token={token}
        connect
        data-lk-theme="default"
        style={{ display: 'flex', flexDirection: 'column', flex: 1, position: 'relative' }}
        options={{ adaptiveStream: true, dynacast: true }}
        onError={(error) => {
          console.error('LiveKit error', error);
        }}
        onMediaDeviceFailure={(failure, kind) => {
          const deviceLabel =
            kind === 'audioinput' ? 'microphone' : kind === 'videoinput' ? 'camera' : 'media device';
          let message = `Unable to access your ${deviceLabel}.`;
          if (failure === MediaDeviceFailure.PermissionDenied) {
            message = `Permission to use your ${deviceLabel} was denied. Please allow access in your browser settings.`;
          } else if (failure === MediaDeviceFailure.NotFound) {
            message = `No ${deviceLabel} was found. Please make sure a ${deviceLabel} is connected and try again.`;
          } else if (failure === MediaDeviceFailure.DeviceInUse) {
            message = `Your ${deviceLabel} is currently in use by another application. Close it and try again.`;
          }
          toast.error(message);
        }}
      >
        <ConnectionStateBridge call={call} onConnectionStateChange={onConnectionStateChange} />
        <RoomAudioRenderer />
        <StageLayout />
        <SessionControls call={call} onLeave={onLeave} />
      </LiveKitRoom>
    </div>
  );
}

function ConnectionStateBridge({
  onConnectionStateChange,
  call,
}: {
  onConnectionStateChange?: (state: ConnectionState) => void;
  call: Call;
}) {
  const room = useRoomContext();

  useEffect(() => {
    if (!room) {
      onConnectionStateChange?.(ConnectionState.Disconnected);
      return;
    }

    const notify = (state: ConnectionState) => {
      onConnectionStateChange?.(state);
    };

    const handleStateChange = () => {
      const state = room.state;
      notify(state);
    };

    notify(room.state);
    room.on(RoomEvent.ConnectionStateChanged, handleStateChange);

    return () => {
      room.off(RoomEvent.ConnectionStateChanged, handleStateChange);
    };
  }, [room, onConnectionStateChange]);

  const state = room?.state ?? ConnectionState.Disconnected;

  return <ConnectionBanner call={call} state={state} />;
}
